import {
  Audience,
  AudienceFilter,
  AudienceFilterOperator,
  AudienceFilterTimeOperator,
  AudienceFilterTimeValue,
  AudienceFilterType,
  PurchasePropertiesModel,
} from '../../audience/components/audience.model';
import { DataPlatformAudienceRule } from '../data-platform.model';

type Timeframe = Pick<DataPlatformAudienceRule, 'daysAgo' | 'daysAgoUpper' | 'startDate' | 'endDate'>;

type Range = Pick<DataPlatformAudienceRule, 'operator' | 'upperValue'> & { value: number };

/** Reach for these comes from list totals or factor shares, not a rule count. */
const NOT_RULE_COUNTED_TYPES: ReadonlySet<string> = new Set([
  AudienceFilterType.AppList,
  AudienceFilterType.AppSegment,
  AudienceFilterType.AppLabels,
  AudienceFilterType.AppTags,
  AudienceFilterType.Country,
  AudienceFilterType.Region,
  AudienceFilterType.Weather,
  AudienceFilterType.Temperature,
]);

export interface AudienceCountRules {
  rules: DataPlatformAudienceRule[];
  /** False for login state, which is per visit, and filters the onsite check never matches. */
  countable: boolean;
}

export function audienceCountRules(audience: Audience | null | undefined): AudienceCountRules {
  const rules: DataPlatformAudienceRule[] = [];
  for (const filter of audience?.filters ?? []) {
    if (NOT_RULE_COUNTED_TYPES.has(filter.type ?? '')) {
      continue;
    }
    const mapped = comparableRules(filter);
    if (!mapped) {
      return { rules: [], countable: false };
    }
    rules.push(...mapped);
  }
  return { rules, countable: true };
}

/** A filter as DataPlatform rules, read like the onsite check; null when the check never matches it. */
export function comparableRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  switch (filter.type) {
    case AudienceFilterType.Identified: {
      const identified = operatorOf(filter) === AudienceFilterOperator.Equals ? parseBool(filter.value) : null;
      return identified === null ? null : [{ type: identified ? 'is_identified' : 'is_anonymous' }];
    }
    case AudienceFilterType.Consent:
      return consentRules(filter);
    case AudienceFilterType.Goals:
      return goalRules(filter);
    case AudienceFilterType.Purchase:
      return purchaseRules(filter);
    case AudienceFilterType.Tags: {
      const tags = matchedValues(filter);
      return tags ? [{ type: tags.negated ? 'has_not_tag' : 'has_tag', values: tags.values }] : null;
    }
    case AudienceFilterType.InterestProfiles: {
      const ids = matchedValues(filter);
      return ids
        ? [{ type: 'interest_profile', operator: ids.negated ? 'not_any_of' : 'any_of', values: ids.values }]
        : null;
    }
    case AudienceFilterType.NpsScore:
    case AudienceFilterType.CsatScore:
      return scoreRules(filter);
    case AudienceFilterType.LatestSession:
    case AudienceFilterType.FirstSeen: {
      const range = rangeOf(filter, 'lte');
      if (!range) {
        return null;
      }
      return [
        {
          type: filter.type === AudienceFilterType.LatestSession ? 'last_active' : 'first_seen',
          operator: range.operator,
          daysAgo: Math.round(range.value),
          ...(range.upperValue === undefined ? {} : { daysAgoUpper: Math.round(range.upperValue) }),
        },
      ];
    }
    case AudienceFilterType.EventCount: {
      const range = rangeOf(filter);
      const timeframe = timeframeOf(filter);
      const eventName = filter.eventProperties?.eventName;
      if (!range || !timeframe) {
        return null;
      }
      return [
        eventName
          ? { type: 'event_count', eventName, ...range, ...timeframe }
          : { type: 'total_events', ...range, ...timeframe },
      ];
    }
    case AudienceFilterType.SessionCount: {
      const range = rangeOf(filter);
      const timeframe = timeframeOf(filter);
      return range && timeframe ? [{ type: 'session_count', ...range, ...timeframe }] : null;
    }
    case AudienceFilterType.WidgetEventCount: {
      const range = rangeOf(filter);
      const timeframe = timeframeOf(filter);
      const widgetId = filter.widgetEventProperties?.widgetId;
      if (!range || !timeframe || widgetId == null) {
        return null;
      }
      const widgetEventType = filter.widgetEventProperties?.widgetEventType;
      return [
        {
          type: 'widget_event_count',
          widgetId,
          ...(widgetEventType ? { widgetEventType } : {}),
          ...range,
          ...timeframe,
        },
      ];
    }
    case AudienceFilterType.Dimension:
    case AudienceFilterType.DimensionAffinity: {
      const range = rangeOf(filter, filter.type === AudienceFilterType.Dimension ? 'gte' : 'eq');
      const dimension = filter.dimensionProperties?.dimension;
      if (!range || !dimension) {
        return null;
      }
      return [
        {
          type: filter.type === AudienceFilterType.Dimension ? 'dimensions' : 'dimension_affinity',
          dimension,
          ...range,
        },
      ];
    }
    case AudienceFilterType.EngagementScore: {
      const range = rangeOf(filter);
      return range ? [{ type: 'engagement_score', ...range }] : null;
    }
    default:
      return null;
  }
}

function consentRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  if (operatorOf(filter) === AudienceFilterOperator.Exists) {
    const expectsConsent = parseBool(filter.value);
    return expectsConsent === null ? null : [{ type: expectsConsent ? 'has_consent' : 'has_not_consent' }];
  }
  const versions = matchedValues(filter);
  return versions ? [{ type: versions.negated ? 'has_not_consent' : 'has_consent', values: versions.values }] : null;
}

/** Goals in the window counted together; "not" means none of them in the window, whatever the frequency. */
function goalRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  const goals = matchedValues(filter);
  const timeframe = timeframeOf(filter);
  if (!goals || !timeframe) {
    return null;
  }
  const names = goals.values.length === 1 ? { eventName: goals.values[0] } : { values: goals.values };
  if (goals.negated) {
    return [{ type: 'goal_count', ...names, operator: 'eq', value: 0, ...timeframe }];
  }
  const frequency = frequencyOf(filter);
  return frequency ? [{ type: 'goal_count', ...names, ...frequency, ...timeframe }] : null;
}

function purchaseRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  const timeframe = timeframeOf(filter);
  const properties = filter.purchaseProperties;
  if (!timeframe) {
    return null;
  }
  const operator = operatorOf(filter);
  if (operator === AudienceFilterOperator.NotEquals) {
    // With no purchase the check sums no revenue and finds no coupon, so these filters match nobody.
    if (properties?.appliedCoupon || (properties && !revenueComparison(properties).holdsAtZero)) {
      return null;
    }
    return [{ type: 'purchase_count', operator: 'eq', value: 0, ...timeframe }];
  }
  const frequency = operator === AudienceFilterOperator.Equals ? frequencyOf(filter) : null;
  if (!frequency) {
    return null;
  }
  const rules: DataPlatformAudienceRule[] = [{ type: 'purchase_count', ...frequency, ...timeframe }];
  if (properties?.appliedCoupon) {
    rules.push({ type: 'purchase_count', operator: 'gte', value: 1, couponOnly: true, ...timeframe });
  }
  if (properties) {
    const revenue = revenueComparison(properties);
    if (!revenue.range) {
      return null;
    }
    rules.push({ type: 'revenue', ...revenue.range, ...timeframe });
  }
  return rules;
}

function scoreRules(filter: AudienceFilter): DataPlatformAudienceRule[] | null {
  const operator = operatorOf(filter);
  const score = Number(filter.value);
  const type = filter.type === AudienceFilterType.NpsScore ? 'nps_score' : 'csat_score';
  if (!Number.isInteger(score) || String(filter.value).trim() === '') {
    return null;
  }
  if (operator === AudienceFilterOperator.GreaterThan) {
    return [{ type, operator: 'gt', value: score }];
  }
  return operator === AudienceFilterOperator.LessThan ? [{ type, operator: 'lt', value: score }] : null;
}

/** A frequency type without a value never matches on the check; a missing type means at least once. */
export function frequencyOf(filter: AudienceFilter): Range | null {
  if (!filter.frequencyType) {
    return { operator: 'gte', value: 1 };
  }
  if (filter.frequencyValue == null) {
    return null;
  }
  if (filter.frequencyType === 'Exactly') {
    // The check also requires at least one, so exactly zero matches nobody.
    return filter.frequencyValue >= 1 ? { operator: 'eq', value: filter.frequencyValue } : null;
  }
  return { operator: 'gte', value: Math.max(filter.frequencyValue, 1) };
}

/** The revenue enum's first member is Exactly, so a missing operator reads as Exactly. */
export function revenueComparison(properties: PurchasePropertiesModel): { range: Range | null; holdsAtZero: boolean } {
  const value = Number(properties.value ?? 0);
  switch (properties.operator ?? 'Exactly') {
    case 'Exactly':
      return { range: { operator: 'eq', value }, holdsAtZero: value === 0 };
    case 'MoreThan':
      return { range: { operator: 'gt', value }, holdsAtZero: 0 > value };
    case 'LessThan':
      return { range: { operator: 'lt', value }, holdsAtZero: 0 < value };
    case 'Between': {
      if (properties.upperValue == null) {
        return { range: { operator: 'gte', value }, holdsAtZero: value <= 0 };
      }
      const upperValue = Number(properties.upperValue);
      return { range: { operator: 'between', value, upperValue }, holdsAtZero: value <= 0 && upperValue >= 0 };
    }
    default:
      return { range: null, holdsAtZero: false };
  }
}

/** "Days since" filters read Equals as at most, as the check does. */
function rangeOf(filter: AudienceFilter, equalsMeans: 'eq' | 'lte' | 'gte' = 'eq'): Range | null {
  const value = parseNumber(filter.value);
  if (value === null) {
    return null;
  }
  switch (operatorOf(filter)) {
    case AudienceFilterOperator.Equals:
      return { operator: equalsMeans, value };
    case AudienceFilterOperator.NotEquals:
      return { operator: 'neq', value };
    case AudienceFilterOperator.GreaterThan:
      return { operator: 'gt', value };
    case AudienceFilterOperator.LessThan:
      return { operator: 'lt', value };
    case AudienceFilterOperator.GreaterThanOrEqual:
      return { operator: 'gte', value };
    case AudienceFilterOperator.LessThanOrEqual:
      return { operator: 'lte', value };
    case AudienceFilterOperator.Between:
      return filter.upperValue == null ? null : { operator: 'between', value, upperValue: filter.upperValue };
    default:
      return null;
  }
}

/** Equals and NotEquals take one value (the check rejects a list); AnyOf and NotAnyOf a comma list. */
export function matchedValues(filter: AudienceFilter): { values: string[]; negated: boolean } | null {
  const operator = operatorOf(filter);
  const single = operator === AudienceFilterOperator.Equals || operator === AudienceFilterOperator.NotEquals;
  const list = operator === AudienceFilterOperator.AnyOf || operator === AudienceFilterOperator.NotAnyOf;
  if (!single && !list) {
    return null;
  }
  const raw = Array.isArray(filter.value) ? filter.value.map(String).join(',') : String(filter.value ?? '');
  if (single && raw.includes(',')) {
    return null;
  }
  const values = raw
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  return values.length === 0
    ? null
    : {
        values,
        negated: operator === AudienceFilterOperator.NotEquals || operator === AudienceFilterOperator.NotAnyOf,
      };
}

/** No time operator is any time; one without a readable value never matches on the check. */
export function timeframeOf(filter: AudienceFilter): Timeframe | null {
  const operator = filter.timeOperator;
  if (!operator || operator === AudienceFilterTimeOperator.AnyTime) {
    return {};
  }
  const value = parseTimeValue(filter.timeValue);
  if (!value) {
    return null;
  }
  switch (operator) {
    case AudienceFilterTimeOperator.TimeWindow:
      return isWholeNumber(value.days) ? { daysAgo: value.days } : null;
    case AudienceFilterTimeOperator.BetweenDaysAgo:
      return isWholeNumber(value.days) && isWholeNumber(value.upperDays)
        ? { daysAgo: value.days, daysAgoUpper: value.upperDays }
        : null;
    case AudienceFilterTimeOperator.SinceDate: {
      const startDate = isoInstant(value.startDate);
      return startDate ? { startDate } : null;
    }
    case AudienceFilterTimeOperator.BetweenDates: {
      const startDate = isoInstant(value.startDate);
      const endDate = isoInstant(value.endDate);
      return startDate && endDate ? { startDate, endDate } : null;
    }
    default:
      return null;
  }
}

function parseTimeValue(raw: AudienceFilter['timeValue']): AudienceFilterTimeValue | null {
  if (raw && typeof raw === 'object') {
    return lowerCaseKeys(raw as Record<string, unknown>);
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? lowerCaseKeys(parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The check reads the time value case-insensitively. */
function lowerCaseKeys(value: Record<string, unknown>): AudienceFilterTimeValue {
  const byKey = new Map(Object.entries(value).map(([key, entry]) => [key.toLowerCase(), entry]));
  const numberAt = (key: string) => {
    const entry = byKey.get(key);
    return typeof entry === 'number' ? entry : undefined;
  };
  const textAt = (key: string) => {
    const entry = byKey.get(key);
    return typeof entry === 'string' ? entry : undefined;
  };
  return {
    days: numberAt('days'),
    upperDays: numberAt('upperdays'),
    startDate: textAt('startdate'),
    endDate: textAt('enddate'),
  };
}

/** The server parses a date without an offset as UTC, so it is sent as that instant. */
export function isoInstant(value: string | Date | undefined): string | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value.toISOString();
  }
  const hasZone = /(Z|[+-]\d\d:?\d\d)$/i.test(value);
  const hasTime = value.includes('T');
  const date = new Date(hasTime && !hasZone ? `${value}Z` : value);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

function isWholeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseBool(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  return text === 'true' ? true : text === 'false' ? false : null;
}

/** The C# operator enum's first member is Equals, so a missing operator reads as Equals. */
export function operatorOf(filter: AudienceFilter): AudienceFilterOperator {
  return filter.operator ?? AudienceFilterOperator.Equals;
}
